import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { COMPANY, STATS, SERVICES, MANIFESTO, SECTORS } from "../data/content";
import { MaskLine, Reveal, stagger, viewport, EASE } from "../lib/motion";
import ServiceIcon from "../components/ServiceIcon";
import SectorMarquee from "../components/SectorMarquee";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000"
          alt="Sundrop facility services"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(37,99,235,0.22),_transparent_60%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-end px-5 pb-16 pt-32 md:px-10 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-6 text-xs md:text-sm uppercase tracking-[0.3em] font-bold text-accent"
        >
          Integrated Facility & Manpower Services &middot; Since {COMPANY.since}
        </motion.p>

        <h1 className="font-display text-5xl font-black leading-[0.92] tracking-tighter md:text-7xl lg:text-8xl">
          <MaskLine delay={0.3}>Mechanised</MaskLine>
          <MaskLine delay={0.42}>facility services,</MaskLine>
          <MaskLine delay={0.54} className="text-accent">done professionally.</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
          className="mt-9 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-xl text-base md:text-lg text-neutral-300 leading-relaxed">
            Housekeeping, security, healthcare, technical and industrial workforce -
            powered by machines, run on process, and delivered across {COMPANY.cities.join(", ")}.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-black transition-transform duration-200 hover:scale-[0.97]"
            >
              Explore Services <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-quote-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10"
            >
              Request a Quote <ArrowUpRight size={16} />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section data-testid="stats" className="border-y border-white/10 bg-[#080808]">
      <div className="mx-auto grid max-w-[1400px] grid-cols-2 md:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal
            key={i}
            delay={i * 0.08}
            className="border-white/10 px-6 py-10 md:py-14 [&:not(:last-child)]:border-r [&:nth-child(-n+2)]:border-b md:[&:nth-child(-n+2)]:border-b-0"
          >
            <p className="font-display text-4xl md:text-6xl font-black tracking-tight text-accent">
              {s.value}
            </p>
            <p className="mt-2 text-sm text-neutral-400">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ServicesOverview() {
  return (
    <section data-testid="services-overview" className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
      <Reveal className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-accent">What we do</p>
          <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tight leading-none">
            Six verticals, <br /> one accountable partner.
          </h2>
        </div>
        <Link to="/services" className="group inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-accent">
          View all services
          <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
        className="grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3"
      >
        {SERVICES.map((s) => (
          <motion.div
            key={s.id}
            variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } } }}
          >
            <Link
              to={`/services/${s.id}`}
              data-testid={`service-card-${s.id}`}
              className="group relative block h-full overflow-hidden bg-[#0a0a0a] p-8 transition-colors duration-300"
            >
              <img
                src={s.image}
                alt={s.title}
                className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-0 grayscale transition-opacity duration-500 group-hover:opacity-20"
              />
              <div className="relative z-10 flex h-full flex-col">
                <div className="flex items-center justify-between">
                  <span className="font-display text-sm font-bold text-neutral-600">{s.index}</span>
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-accent transition-colors duration-300 group-hover:bg-accent group-hover:text-black">
                    <ServiceIcon name={s.icon} size={20} />
                  </span>
                </div>
                <h3 className="font-display mt-16 text-2xl md:text-3xl font-bold tracking-tight">{s.title}</h3>
                <p className="mt-3 flex-1 text-sm text-neutral-400 leading-relaxed">{s.short}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                  Learn more <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

function Manifesto() {
  return (
    <section data-testid="manifesto" className="border-t border-white/10 bg-[#080808]">
      <div className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-accent">Why Sundrop</p>
          <h2 className="font-display mt-4 max-w-2xl text-4xl md:text-6xl font-black tracking-tight leading-none">
            Tech-based services, run smoothly.
          </h2>
        </Reveal>

        <div className="mt-16 divide-y divide-white/10 border-t border-white/10">
          {MANIFESTO.map((m) => (
            <Reveal key={m.num}>
              <div className="grid gap-4 py-10 md:grid-cols-[auto_1fr] md:gap-16">
                <span className="font-display text-6xl md:text-8xl font-black leading-none text-white/10">
                  {m.num}
                </span>
                <div className="md:pt-2">
                  <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{m.title}</h3>
                  <p className="mt-3 max-w-2xl text-base md:text-lg text-neutral-400 leading-relaxed">{m.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectorsPreview() {
  return (
    <section data-testid="sectors-preview" className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
      <Reveal className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-accent">Where we work</p>
          <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tight leading-none">
            Sectors we serve.
          </h2>
        </div>
        <Link to="/sectors" className="group inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-accent">
          All sectors <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SECTORS.slice(0, 4).map((s, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <div className="group relative aspect-[3/4] overflow-hidden rounded-xl border border-white/10">
              <img src={s.image} alt={s.name} className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
              <div className="absolute bottom-0 p-5">
                <h3 className="font-display text-xl font-bold">{s.name}</h3>
                <p className="text-xs text-neutral-300">{s.detail}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Stats />
      <ServicesOverview />
      <SectorMarquee />
      <Manifesto />
      <SectorsPreview />
    </div>
  );
}
